<template><div><code-well-header>
  <dt-datepicker></dt-datepicker>
</code-well-header>
<h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">We recommend to wrap the datepicker in a <code v-pre>dt-popover</code> component.</p>
<p class="d-docsite--paragraph">It is required to provide the component with proper labels for i18n and accessibility.</p>
<p class="d-docsite--paragraph">The component will return a <a href="https://www.w3schools.com/js/js_dates.asp" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Date object</a> object when a date is selected.</p>
<p class="d-docsite--paragraph">For the <code v-pre>withPopover</code> variant it is necessary to provide the <code v-pre>initial-focus-element=&quot;#prevYearButton&quot;</code> prop
to the <code v-pre>dt-popover</code> component.</p>
<p class="d-docsite--paragraph">With this we accomplish the requirement to have the previous year button focused when the datepicker is opened.</p>
<h2 id="variants-and-examples" tabindex="-1" class="d-docsite--header-2">Variants and Examples <a class="header-anchor d-link d-docsite--link d-link" href="#variants-and-examples" aria-hidden="true">#</a></h2>
<h3 id="default" tabindex="-1" class="d-docsite--header-3">Default <a class="header-anchor d-link d-docsite--link d-link" href="#default" aria-hidden="true">#</a></h3>
<code-well-header>
  <dt-datepicker></dt-datepicker>
</code-well-header>
<code-example-tabs
htmlCode='
<div class="d-stack d-stack--gap-400 d-datepicker">
  <div class="d-datepicker__hd">
    <div class="d-stack d-stack--row d-stack--gap-300 d-datepicker__month-year">
      <nav class="d-stack d-stack--row d-stack--gap-200 d-datepicker__nav">
        <div data-qa="dt-tooltip-container">
          <span data-qa="dt-tooltip-anchor">
            <button class="base-button__button d-btn d-btn--muted d-btn--xs d-btn--circle d-datepicker__nav-btn" data-qa="dt-button" type="button" aria-label="Change to Previous year 2023" id="prevYearButton">
              <span data-qa="dt-button-label" class="d-btn__label base-button__label">
                <svg>...</svg>
              </span>
            </button>
          </span>
        </div>
        <div data-qa="dt-tooltip-container">
          <span data-qa="dt-tooltip-anchor">
            <button class="base-button__button d-btn d-btn--muted d-btn--xs d-btn--circle d-datepicker__nav-btn" data-qa="dt-button" type="button" aria-label="Change to Previous month February" id="prevMonthButton">
              <span data-qa="dt-button-label" class="d-btn__label base-button__label">
                <svg>...</svg>
              </span>
            </button>
          </span>
        </div>
      </nav>
      <div id="calendar-heading" class="d-datepicker__month-year-title">March 2024</div>
      <nav class="d-stack d-stack--row d-stack--gap-200 d-datepicker__nav">
        <div data-qa="dt-tooltip-container">
          <span data-qa="dt-tooltip-anchor">
            <button class="base-button__button d-btn d-btn--muted d-btn--xs d-btn--circle d-datepicker__nav-btn" data-qa="dt-button" type="button" aria-label="Change to Next month April" id="nextMonthButton">
              <span data-qa="dt-button-label" class="d-btn__label base-button__label">
                <svg>...</svg>
              </span>
            </button>
          </span>
        </div>
        <div data-qa="dt-tooltip-container">
          <span data-qa="dt-tooltip-anchor">
            <button class="base-button__button d-btn d-btn--muted d-btn--xs d-btn--circle d-datepicker__nav-btn" data-qa="dt-button" type="button" aria-label="Change to Next year 2025" id="nextYearButton">
              <span data-qa="dt-button-label" class="d-btn__label base-button__label">
                <svg>...</svg>
              </span>
            </button>
          </span>
        </div>
      </nav>
    </div>
  </div>
  <div class="d-datepicker__bd">
    <table class="d-datepicker__calendar" aria-labelledby="calendar-heading">
      <thead>
        <tr>
          <th scope="col" class="d-datepicker__cell d-datepicker__cell--header"><span class="d-datepicker__weekday" title="Su" aria-label="Su">Su</span></th>
          <th scope="col" class="d-datepicker__cell d-datepicker__cell--header"><span class="d-datepicker__weekday" title="Mo" aria-label="Mo">Mo</span></th>
          ...
        </tr>
      </thead>
      <tbody>
        <tr>
          <td class="d-datepicker__cell" role="listbox">
            <button class="base-button__button d-btn d-btn--sm d-btn--circle d-datepicker__day d-datepicker__day--disabled" data-qa="dt-button" type="button" disabled="" aria-label="Select day 25 February 2024" aria-selected="false" role="option">
              <span data-qa="dt-button-label" class="d-btn__label base-button__label">25</span>
            </button>
          </td>
          <td class="d-datepicker__cell" role="listbox">
            <button class="base-button__button d-btn d-btn--sm d-btn--circle d-datepicker__day d-datepicker__day--disabled" data-qa="dt-button" type="button" disabled="" aria-label="Select day 26 February 2024" aria-selected="false" role="option">
              <span data-qa="dt-button-label" class="d-btn__label base-button__label">26</span>
            </button>
          </td>
          <td class="d-datepicker__cell" role="listbox">
            <button class="base-button__button d-btn d-btn--sm d-btn--circle d-datepicker__day d-datepicker__day--disabled" data-qa="dt-button" type="button" disabled="" aria-label="Select day 27 February 2024" aria-selected="false" role="option">
              <span data-qa="dt-button-label" class="d-btn__label base-button__label">27</span>
            </button>
          </td>
          <td class="d-datepicker__cell" role="listbox">
            <button class="base-button__button d-btn d-btn--sm d-btn--circle d-datepicker__day d-datepicker__day--disabled" data-qa="dt-button" type="button" disabled="" aria-label="Select day 28 February 2024" aria-selected="false" role="option">
              <span data-qa="dt-button-label" class="d-btn__label base-button__label">28</span>
            </button>
          </td>
          <td class="d-datepicker__cell" role="listbox">
            <button class="base-button__button d-btn d-btn--sm d-btn--circle d-datepicker__day d-datepicker__day--disabled" data-qa="dt-button" type="button" disabled="" aria-label="Select day 29 February 2024" aria-selected="false" role="option">
              <span data-qa="dt-button-label" class="d-btn__label base-button__label">29</span>
            </button>
          </td>
          <td class="d-datepicker__cell" role="listbox">
            <button class="base-button__button d-btn d-btn--sm d-btn--circle d-datepicker__day" data-qa="dt-button" type="button" aria-label="Select day 1 March 2024" aria-selected="false" role="option">
              <span data-qa="dt-button-label" class="d-btn__label base-button__label">1</span>
            </button>
          </td>
          <td class="d-datepicker__cell" role="listbox">
            <button class="base-button__button d-btn d-btn--sm d-btn--circle d-datepicker__day" data-qa="dt-button" type="button" aria-label="Select day 2 March 2024" aria-selected="false" role="option">
              <span data-qa="dt-button-label" class="d-btn__label base-button__label">2</span>
            </button>
          </td>
        </tr>
        <tr>
          <td class="d-datepicker__cell" role="listbox">
            <button class="base-button__button d-btn d-btn--sm d-btn--circle d-datepicker__day" data-qa="dt-button" type="button" aria-label="Select day 3 March 2024" aria-selected="false" role="option">
              <span data-qa="dt-button-label" class="d-btn__label base-button__label">3</span>
            </button>
          </td>
          <td class="d-datepicker__cell" role="listbox">
            <button class="base-button__button d-btn d-btn--sm d-btn--circle d-datepicker__day" data-qa="dt-button" type="button" aria-label="Select day 4 March 2024" aria-selected="false" role="option">
              <span data-qa="dt-button-label" class="d-btn__label base-button__label">4</span>
            </button>
          </td>
          <td class="d-datepicker__cell" role="listbox">
            <button class="base-button__button d-btn d-btn--sm d-btn--circle d-datepicker__day" data-qa="dt-button" type="button" aria-label="Select day 5 March 2024" aria-selected="false" role="option">
              <span data-qa="dt-button-label" class="d-btn__label base-button__label">5</span>
            </button>
          </td>
          ...
        </tr>
        <tr>
          ...
        </tr>
        ...
      </tbody>
    </table>
  </div>
</div>
'
vueCode='
<dt-datepicker :selected-date="new Date()"></dt-datepicker>
'
showHtmlWarning />
<h3 id="with-popover" tabindex="-1" class="d-docsite--header-3">With Popover <a class="header-anchor d-link d-docsite--link d-link" href="#with-popover" aria-hidden="true">#</a></h3>
<code-well-header>
  <dt-popover
    :open="datepickerOpened"
    initial-focus-element="#prevYearButton"
    padding="none"
    @opened="(open) => { datepickerOpened = open }"
    placement="bottom-start"
  >
    <template #anchor>
      <dt-button
        size="sm"
        circle
        importance="clear"
        aria-label="Open datepicker"
        @click="toggleDatepicker"
      >
        <template #icon>
          <dt-icon
            name="calendar"
            size="300"
          />
        </template>
      </dt-button>
    </template>
    <template #content>
      <dt-datepicker></dt-datepicker>
    </template>
  </dt-popover>
</code-well-header>
<code-example-tabs
htmlCode='
<div data-qa="dt-popover-content" class="d-popover__content">
  <div class="d-stack d-stack--gap-400 d-datepicker">
    <div class="d-datepicker__hd">
      <div class="d-stack d-stack--row d-stack--gap-300 d-datepicker__month-year">
        <nav class="d-stack d-stack--row d-stack--gap-200 d-datepicker__nav">
          <div>
            <span>
              <button
                class="base-button__button d-btn d-btn--muted d-btn--xs d-btn--circle d-datepicker__nav-btn"
                type="button"
                aria-label="Change to Previous year 2023"
              >
                <span class="d-btn__label base-button__label">
                  <svg>...</svg>
                </span>
              </button>
            </span>
          </div>
          <div>
            <span>
              <button
                class="base-button__button d-btn d-btn--muted d-btn--xs d-btn--circle d-datepicker__nav-btn"
                type="button"
                aria-label="Change to Previous month June"
              >
                <span class="d-btn__label base-button__label">
                  <svg>...</svg>
                </span>
              </button>
            </span>
          </div>
        </nav>
        <div class="d-datepicker__month-year-title">July 2024</div>
        <nav class="d-stack d-stack--row d-stack--gap-200 d-datepicker__nav">
          <div>
            <span>
              <button
                class="base-button__button d-btn d-btn--muted d-btn--xs d-btn--circle d-datepicker__nav-btn"
                type="button"
                aria-label="Change to Next month August"
              >
                <span class="d-btn__label base-button__label">
                  <svg>...</svg>
                </span>
              </button>
            </span>
          </div>
          <div>
            <span>
              <button
                class="base-button__button d-btn d-btn--muted d-btn--xs d-btn--circle d-datepicker__nav-btn"
                type="button"
                aria-label="Change to Next year 2025"
              >
                <span class="d-btn__label base-button__label">
                  <svg>...</svg>
                </span>
              </button>
            </span>
          </div>
        </nav>
      </div>
    </div>
    <div class="d-datepicker__bd">
      <table class="d-datepicker__calendar" aria-labelledby="calendar-heading">
        <thead>
          <tr>
            <th scope="col" class="d-datepicker__cell d-datepicker__cell--header">
              <span class="d-datepicker__weekday" title="Su" aria-label="Su">
                Su
              </span>
            </th>
            <th scope="col" class="d-datepicker__cell d-datepicker__cell--header">
              <span class="d-datepicker__weekday" title="Mo" aria-label="Mo">
                Mo
              </span>
            </th>
            <th scope="col" class="d-datepicker__cell d-datepicker__cell--header">
              <span class="d-datepicker__weekday" title="Tu" aria-label="Tu">
                Tu
              </span>
            </th>
            <th scope="col" class="d-datepicker__cell d-datepicker__cell--header">
              <span class="d-datepicker__weekday" title="We" aria-label="We">
                We
              </span>
            </th>
            <th scope="col" class="d-datepicker__cell d-datepicker__cell--header">
              <span class="d-datepicker__weekday" title="Th" aria-label="Th">
                Th
              </span>
            </th>
            <th scope="col" class="d-datepicker__cell d-datepicker__cell--header">
              <span class="d-datepicker__weekday" title="Fr" aria-label="Fr">
                Fr
              </span>
            </th>
            <th scope="col" class="d-datepicker__cell d-datepicker__cell--header">
              <span class="d-datepicker__weekday" title="Sa" aria-label="Sa">
                Sa
              </span>
            </th>
          </tr>
        </thead>
        <tbody></tbody>
      </table>
    </div>
  </div>
</div>
'
vueCode='
<dt-popover
  :open="datepickerOpened"
  initial-focus-element="#prevYearButton"
  padding="none"
  @opened="(open) => { datepickerOpened = open }"
  placement="bottom-start"
>
  <template #anchor>
    <dt-button
      size="sm"
      circle
      importance="clear"
      aria-label="Open datepicker"
      @click="toggleDatepicker"
    >
      <template #icon>
        <dt-icon
          name="calendar"
          size="300"
        />
      </template>
    </dt-button>
  </template>
  <template #content>
    <dt-datepicker></dt-datepicker>
  </template>
</dt-popover>
'
/>
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="datepicker"></component-vue-api><h2 id="accessibility" tabindex="-1" class="d-docsite--header-2">Accessibility <a class="header-anchor d-link d-docsite--link d-link" href="#accessibility" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Keyboard navigation with arrow and tab keys for datepicker component.</p>
<p class="d-docsite--paragraph">It will switch between <code v-pre>month-year-picker</code> and <code v-pre>calendar</code> with <code v-pre>TAB</code>.</p>
<p class="d-docsite--paragraph">It will move around all calendar days with <code v-pre>arrow-keys</code>.</p>
<p class="d-docsite--paragraph">It will jump from <code v-pre>month-year-picker</code> to <code v-pre>calendar</code> with <code v-pre>DOWN</code> arrow key.</p>
<p class="d-docsite--paragraph">It will jump to <code v-pre>month-year-picker</code> when there is no more days at the bottom or top of the calendar.</p>
<p class="d-docsite--paragraph">It will change year or month with <code v-pre>ENTER</code> or <code v-pre>SPACE</code> (native event)</p>
<p class="d-docsite--paragraph">It will select day with <code v-pre>ENTER</code> or <code v-pre>SPACE</code> (native event)</p>
<p class="d-docsite--paragraph">It will start with focus on previous-year on mounted</p>
<p class="d-docsite--paragraph">Screen reader will say</p>
<p class="d-docsite--paragraph">In <code v-pre>month-year-picker</code>:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Change to previous year, 2022</li>
<li class="d-docsite--list-element">Change to previous month, july</li>
<li class="d-docsite--list-element">Change to next month, september</li>
<li class="d-docsite--list-element">Change to next year, 2024</li>
</ul>
<p class="d-docsite--paragraph">In <code v-pre>calendar</code>:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Select day 20 July 2023</li>
</ul>
<h2 id="date-formats" tabindex="-1" class="d-docsite--header-2">Date Formats <a class="header-anchor d-link d-docsite--link d-link" href="#date-formats" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">The following functions are available for date formatting.</p>
<div class="d-bgc-secondary d-bar8 d-p16">
  <dt-stack
    :direction="{ 'default': 'column', 'md': 'row' }"
    gap="600"
    class="d-ai-flex-start"
  >
    <dt-stack class="d-ai-center">
      <dt-datepicker
        :selected-date="currentSelectedDate"
        @selected-date="currentSelectedDate = $event;"
      />
      <p class="d-body--sm">
        {{ currentSelectedDate }}
      </p>
    </dt-stack>
    <table class="d-table d-body--sm d-bt d-bc-default d-fl1">
      <tr>
        <th scope="row" class="d-code--sm">formatLong</th>
        <td>{{ formatLong(currentSelectedDate) }}</td>
      </tr>
      <tr>
        <th scope="row" class="d-code--sm">formatMedium</th>
        <td>{{ formatMedium(currentSelectedDate) }}</td>
      </tr>
      <tr>
        <th scope="row" class="d-code--sm">formatShort</th>
        <td>{{ formatShort(currentSelectedDate) }}</td>
      </tr>
      <tr>
        <th scope="row" class="d-code--sm">formatShort (no weekday)</th>
        <td>{{ formatShort(currentSelectedDate, false) }}</td>
      </tr>
      <tr>
        <th scope="row" class="d-code--sm">formatNoYear</th>
        <td>{{ formatNoYear(currentSelectedDate) }}</td>
      </tr>
      <tr>
        <th scope="row" class="d-code--sm">formatNoYear (abbreviated)</th>
        <td>{{ formatNoYear(currentSelectedDate, true) }}</td>
      </tr>
      <tr>
        <th scope="row" class="d-code--sm">formatNumerical</th>
        <td>{{ formatNumerical(currentSelectedDate) }}</td>
      </tr>
    </table>
  </dt-stack>
</div>
<!-- TODO: Autogenerate the docs from JSDocs in packages/dialtone-vue2/components/datepicker/formatUtils.js -->
<h3 id="formatlong" tabindex="-1" class="d-docsite--header-3">formatLong <a class="header-anchor d-link d-docsite--link d-link" href="#formatlong" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph"><strong>formatLong</strong>(<code v-pre>date</code>, <code v-pre>locale</code>): <code v-pre>string</code></p>
<p class="d-docsite--paragraph">Formats a date into a long format using the specified locale.</p>
<h4 class="d-docsite--header-4">Parameters</h4>
<table>
<thead>
<tr>
<th style="text-align:left">Name</th>
<th style="text-align:left">Type</th>
<th style="text-align:left">Description</th>
</tr>
</thead>
<tbody>
<tr>
<td style="text-align:left"><code v-pre>date</code></td>
<td style="text-align:left"><code v-pre>Date</code></td>
<td style="text-align:left">The date to format</td>
</tr>
<tr>
<td style="text-align:left"><code v-pre>locale</code></td>
<td style="text-align:left"><code v-pre>string</code></td>
<td style="text-align:left">The locale to use for formatting. Defaults to Dialtone preferred locale.</td>
</tr>
</tbody>
</table>
<h4 class="d-docsite--header-4">Returns</h4>
<p class="d-docsite--paragraph"><code v-pre>string</code></p>
<p class="d-docsite--paragraph">The formatted date string.</p>
<h3 id="formatmedium" tabindex="-1" class="d-docsite--header-3">formatMedium <a class="header-anchor d-link d-docsite--link d-link" href="#formatmedium" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph"><strong>formatMedium</strong>(<code v-pre>date</code>, <code v-pre>locale</code>): <code v-pre>string</code></p>
<p class="d-docsite--paragraph">Formats the given date in medium format.</p>
<h4 class="d-docsite--header-4">Parameters</h4>
<table>
<thead>
<tr>
<th style="text-align:left">Name</th>
<th style="text-align:left">Type</th>
<th style="text-align:left">Description</th>
</tr>
</thead>
<tbody>
<tr>
<td style="text-align:left"><code v-pre>date</code></td>
<td style="text-align:left"><code v-pre>Date</code></td>
<td style="text-align:left">The date to format</td>
</tr>
<tr>
<td style="text-align:left"><code v-pre>locale</code></td>
<td style="text-align:left"><code v-pre>string</code></td>
<td style="text-align:left">The locale to use for formatting. Defaults to Dialtone preferred locale.</td>
</tr>
</tbody>
</table>
<h4 class="d-docsite--header-4">Returns</h4>
<p class="d-docsite--paragraph"><code v-pre>string</code></p>
<p class="d-docsite--paragraph">The formatted date string.</p>
<h3 id="formatshort" tabindex="-1" class="d-docsite--header-3">formatShort <a class="header-anchor d-link d-docsite--link d-link" href="#formatshort" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph"><strong>formatShort</strong>(<code v-pre>date</code>, <code v-pre>locale</code>, <code v-pre>showWeekday</code>= true): <code v-pre>string</code></p>
<p class="d-docsite--paragraph">Formats a date into a short string representation.</p>
<h4 class="d-docsite--header-4">Parameters</h4>
<table>
<thead>
<tr>
<th style="text-align:left">Name</th>
<th style="text-align:left">Type</th>
<th style="text-align:left">Description</th>
</tr>
</thead>
<tbody>
<tr>
<td style="text-align:left"><code v-pre>date</code></td>
<td style="text-align:left"><code v-pre>Date</code></td>
<td style="text-align:left">The date to format</td>
</tr>
<tr>
<td style="text-align:left"><code v-pre>locale</code></td>
<td style="text-align:left"><code v-pre>string</code></td>
<td style="text-align:left">The locale to use for formatting. Defaults to Dialtone preferred locale.</td>
</tr>
<tr>
<td style="text-align:left"><code v-pre>showWeekday</code></td>
<td style="text-align:left"><code v-pre>boolean</code></td>
<td style="text-align:left">Whether to include the weekday in the formatted string. Defaults to true.</td>
</tr>
</tbody>
</table>
<h4 class="d-docsite--header-4">Returns</h4>
<p class="d-docsite--paragraph"><code v-pre>string</code></p>
<p class="d-docsite--paragraph">The formatted date string.</p>
<h3 id="formatnoyear" tabindex="-1" class="d-docsite--header-3">formatNoYear <a class="header-anchor d-link d-docsite--link d-link" href="#formatnoyear" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph"><strong>formatNoYear</strong>(<code v-pre>date</code>, <code v-pre>locale</code>, <code v-pre>abbreviated</code>= false): <code v-pre>string</code></p>
<p class="d-docsite--paragraph">Formats a date without the year.</p>
<h4 class="d-docsite--header-4">Parameters</h4>
<table>
<thead>
<tr>
<th style="text-align:left">Name</th>
<th style="text-align:left">Type</th>
<th style="text-align:left">Description</th>
</tr>
</thead>
<tbody>
<tr>
<td style="text-align:left"><code v-pre>date</code></td>
<td style="text-align:left"><code v-pre>Date</code></td>
<td style="text-align:left">The date to format</td>
</tr>
<tr>
<td style="text-align:left"><code v-pre>locale</code></td>
<td style="text-align:left"><code v-pre>string</code></td>
<td style="text-align:left">The locale to use for formatting. Defaults to Dialtone preferred locale.</td>
</tr>
<tr>
<td style="text-align:left"><code v-pre>abbreviated</code></td>
<td style="text-align:left"><code v-pre>boolean</code></td>
<td style="text-align:left">Whether to use abbreviated month names. Defaults to false.</td>
</tr>
</tbody>
</table>
<h4 class="d-docsite--header-4">Returns</h4>
<p class="d-docsite--paragraph"><code v-pre>string</code></p>
<p class="d-docsite--paragraph">The formatted date without the year.</p>
<h3 id="formatnumerical" tabindex="-1" class="d-docsite--header-3">formatNumerical <a class="header-anchor d-link d-docsite--link d-link" href="#formatnumerical" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph"><strong>formatNumerical</strong>(<code v-pre>date</code>, <code v-pre>locale</code>): <code v-pre>string</code></p>
<p class="d-docsite--paragraph">Formats a date into a numerical string representation.</p>
<h4 class="d-docsite--header-4">Parameters</h4>
<table>
<thead>
<tr>
<th style="text-align:left">Name</th>
<th style="text-align:left">Type</th>
<th style="text-align:left">Description</th>
</tr>
</thead>
<tbody>
<tr>
<td style="text-align:left"><code v-pre>date</code></td>
<td style="text-align:left"><code v-pre>Date</code></td>
<td style="text-align:left">The date to format</td>
</tr>
<tr>
<td style="text-align:left"><code v-pre>locale</code></td>
<td style="text-align:left"><code v-pre>string</code></td>
<td style="text-align:left">The locale to use for formatting. Defaults to Dialtone preferred locale.</td>
</tr>
</tbody>
</table>
<h4 class="d-docsite--header-4">Returns</h4>
<p class="d-docsite--paragraph"><code v-pre>string</code></p>
<p class="d-docsite--paragraph">The formatted numerical date string.</p>
</div></template>


<script setup>
import { ref, inject } from 'vue';

const dialtoneUtils = inject('dialtoneUtils');
const { formatLong, formatMedium, formatShort, formatNoYear, formatNumerical } = dialtoneUtils;

const currentSelectedDate = ref(new Date());
const datepickerOpened = ref(false);

const toggleDatepicker = () => {
  datepickerOpened.value = !datepickerOpened.value;
};

</script>